import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { playerService, agentService, clubService } from "../../services/api";
import toast from "react-hot-toast";
import { ROLES } from "../../config/roles";

import AgentHeroBanner from "../../components/common/AgentHeroBanner";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import AgentPlayerCard from "../../components/agent/AgentPlayerCard";
import MessageRequestComposeModal, {
  RequestPendingBadge,
} from "../../components/inbox/MessageRequestComposeModal";
import messageRequestService from "../../services/messageRequestApi";
import { DETAILED_POSITION_OPTIONS } from "../../components/common/DetailedPositionsControl";
import PositionFilterDropdown from "../../components/common/PositionFilterDropdown";
import { useUiLanguage } from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation, getStaticTemplateMatch } from "../../translations/registerStaticTranslations";

// DECISION-042 (Marin, 2026-07-29): an agent WITHOUT an agency renders nothing
// for it — no label, no dash. `GET /dashboard/agents/public/<id>/` answers
// `agency: {}` for such an agent (views.py: `AgencyListSerializer(...) if
// agent.agency else {}`), but this mapper used to build an agency object
// regardless, with `name: "-"` standing in for the missing name. Every
// downstream absence check therefore read PRESENT: the hero banner's
// `hasAgency` guard (FE PR #271) never fired on the real page, and the
// compose-modal subtitle's `.filter(Boolean)` kept the dash. No agency in the
// payload → NO agency object, so the absence survives the mapping.
// Exported for the test that pins that behaviour.
export const mapAgencyPayload = (agency) => {
  if (!agency?.name) return null;
  return {
    agency_id: agency.agency_id || agency.id,
    name: agency.name,
    has_fifa_license: agency.has_fifa_license || false,
    document_url: agency.document_url,
    profile_picture_url: agency.profile_picture_url,
    // T053: bind to the ONE authoritative figure. The old chain preferred
    // `agency.transfer_points`, which the payload rendered as the wallet row's
    // PRIMARY KEY — so this label showed a database id (e.g. 202) as points.
    // Both fields now carry the agency wallet balance; total_transfer_points is
    // the documented one.
    total_transfer_points: agency.total_transfer_points ?? 0,
    total_agents_count: agency.total_agents_count || 0,
    email: agency.email,
  };
};

const AgentPublicProfile = () => {
  const { agentId } = useParams();
  const { currentRole, roles ,user} = useSelector((state) => state.auth);
  // QAV5-502: window.alert() is a native browser dialog the DOM scanner
  // cannot reach — look the sentence up the same way the scanner does (exact
  // match first, template match second, e.g. for the "{count}" share count).
  const currentLanguage = useUiLanguage();
  const tr = (s) =>
    getStaticTranslation(i18n, s, currentLanguage) ||
    getStaticTemplateMatch(i18n, s, currentLanguage) ||
    s;
  const location = useLocation();
  const [representationRequest, setRepresentationRequest] = useState(
    location.state?.representationRequest || null
  );
  const [agentJoinRequest, setAgentJoinRequest] = useState(
    location.state?.agentJoinRequest || null
  );
  const [selectedPosition, setSelectedPosition] = useState("all");
  const [showShareModal, setShowShareModal] = useState(false);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [showShareModal, setShowShareModal] = useState(false);
  // current user profile previously used; removed to avoid linter warnings
  const navigate = useNavigate();

  // Confirmation modal states
  const [showAcceptConfirmModal, setShowAcceptConfirmModal] = useState(false);
  const [showDeclineConfirmModal, setShowDeclineConfirmModal] = useState(false);

  // Loading states for buttons
  const [isAcceptingPlayer, setIsAcceptingPlayer] = useState(false);
  const [isDismissingPlayer, setIsDismissingPlayer] = useState(false);
  const [isAcceptingAgent, setIsAcceptingAgent] = useState(false);
  const [isDecliningAgent, setIsDecliningAgent] = useState(false);
  const [isStartingChat, setIsStartingChat] = useState(false);

  // Cold-contact gate (design 1i-1l): agent->agent contact goes through a
  // Message Request, not an instant chat. `showComposeModal` opens the compose
  // dialog; once a request is sent the action flips to a terminal "Request
  // pending" chip. Club viewers never hit this path (instant chat, below).
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    console.log("roles in AgentPublicProfile", user);
    const hasAgentRole = roles.includes(ROLES.Agent);
    console.log("Has Agent role:", hasAgentRole);
  }, [user]);

  // 1l: surface the "Request pending" badge on reload, not just in-session. When
  // an agent opens a foreign agent's profile, check their outstanding sent
  // requests once; if one already targets THIS agent (and hasn't been accepted),
  // flip the CTA to the terminal badge so they don't re-request. Degrades
  // silently if the sent payload doesn't carry a recipient id yet (badge simply
  // stays hidden — no regression to the in-session flip via onSent).
  useEffect(() => {
    if (currentRole !== ROLES.Agent) return undefined;
    if (!agent?.agentPk) return undefined;
    if (user?.id != null && agent?.id != null && user.id === agent.id)
      return undefined;

    let cancelled = false;
    (async () => {
      const res = await messageRequestService.listSent();
      if (cancelled || !res?.success) return;
      const rows = res.results || res.data?.results || [];
      const targetsThisAgent = rows.some((r) => {
        const rid =
          r?.recipient_agent_id ?? r?.recipient_agent ?? r?.recipient?.agent_id;
        return (
          rid != null &&
          String(rid) === String(agent.agentPk) &&
          r?.status !== "accepted"
        );
      });
      if (targetsThisAgent) setRequestSent(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [currentRole, agent?.agentPk, agent?.id, user?.id]);

  useEffect(() => {
    const fetchAgentData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await agentService.getAgentPublicProfile(agentId);
        if (response.success) {
          // API returns { results: { agent: { details, agency, players } } }
          console.log("response.data", response.data);
          const agentData = response.data.agent;
          if (agentData) {
            // Map the API response to the expected format
            setAgent({
              id: agentData.details?.account_id || agentId,
              // Agent row PK — what the message-request endpoint keys
              // `recipient_agent_id` by (NOT the account id). Additive field on
              // the public-profile payload; null until the backend stamps it.
              agentPk: agentData.details?.agent_id ?? null,
              name: agentData.details?.name || "-",
              country: agentData.details?.country || null,
              // D-A FIFA Verified badge: the hero renders it only when this
              // is exactly 'fifa_verified' (AgentLicenseVerification).
              license_verification_status:
                agentData.details?.license_verification_status,
              // Marin (D-A QA): the hero's License card prints this raw
              // number (search-table "License No." field); empty omits it.
              license_number: agentData.details?.license_number,
              profile_picture_url:
                agentData.details?.profile_picture_url || null,
              date_of_birth: agentData.details?.date_of_birth,
              agency: mapAgencyPayload(agentData.agency),
              players: (agentData.players || []).map((player) => ({
                account_id: player.account_id,
                date_of_birth: player.date_of_birth,
                position: player.position || "Position not set",
                player_image:
                  player.resolved_player_image || player.player_image,
                country: player.country?.name || "-",
                country_image: player.country?.image,
                current_agent: player.current_agent,
                current_club: player.current_club,
                name: player.name,
                document_url: player.document_url,
                transfermarkt_id: player.transfermarkt_id,
                discovery: player.discovery,
                foot: player.foot,
                height: player.height,
                weight: player.weight,
                wyscout_id: player.wyscout_id,
                is_active_for_agent: player.is_active_for_agent,
                email: player.email,
                phone_number: player.phone_number,
                tp_detailed_position: player.tp_detailed_position,
                active_mandate_id: player.active_mandate_id,
                active_mandate_start_date: player.active_mandate_start_date,
                active_mandate_end_date: player.active_mandate_end_date,
                active_mandate_is_exclusive: player.active_mandate_is_exclusive,
                days_to_mandate_end: player.days_to_mandate_end,
                renewal_approaching: player.renewal_approaching,
                birth_country: {
                  id: player.birth_country?.id,
                  name: player.birth_country?.name,
                  image_url: player.birth_country?.image_url,
                },
                nationality: {
                  id: player.nationality?.id,
                  name: player.nationality?.name,
                  image_url: player.nationality?.image_url,
                },
              })),
            });
          } else {
            setAgent(null);
          }
        } else {
          setError("Failed to load agent profile");
          setAgent(null);
        }
      } catch (error) {
        console.error("Error fetching agent data:", error);
        setError(error.message || "Failed to load agent profile");
        setAgent(null);
      }

      setLoading(false);
    };

    if (agentId) {
      console.log("agentId in useEffect", agentId);
      fetchAgentData();
    }
  }, [agentId]);

  // Fetch current user profile previously used to guard share; removed to avoid unused vars

  const positions = [
    { id: "all", label: "All positions" },
    ...DETAILED_POSITION_OPTIONS.map((option) => ({
      id: option.value,
      label: option.value.toUpperCase(),
      fullLabel: option.label,
    })),
    { id: "unknown", label: "Position not set" },
  ];

  const handleShare = async (selectedUsers, shareUrl) => {
    try {
      console.log("Sharing with users:", selectedUsers);
      console.log("Share URL:", shareUrl);
      alert(tr(`Shared agent profile with ${selectedUsers.length} user(s)!`));
    } catch (error) {
      console.error("Error sharing:", error);
      alert(tr("Failed to share. Please try again."));
    }
  };

  const shareUrl = `${window.location.origin}/search/agent/${agentId}`;

  const handleStartChat = async () => {
    if (!agent?.id || isStartingChat) {
      return;
    }

    setIsStartingChat(true);
    try {
      let response;
      
      // Use different API based on current role
      if (currentRole === ROLES.Agent) {
        // Agent-to-Agent chat
        response = await agentService.createAgentDirectChat(agent.id);
      } else if (currentRole === ROLES.ClubAdmin) {
        // Club-to-Agent chat
        response = await clubService.createAgentDirectChat(agent.id);
      } else {
        toast.error("You don't have permission to message this agent");
        return;
      }

      if (response.success) {
        toast.success("Chat ready in Inbox");
        navigate("/inbox", {
          replace: false,
          state: {
            directChat: {
              channelId: response.data?.channel_id,
              channelType: response.data?.channel_type,
            },
          },
        });
      } else {
        toast.error(response.error || "Failed to start chat");
      }
    } catch (error) {
      console.error("Error creating direct chat:", error);
      toast.error("Failed to start chat. Please try again.");
    } finally {
      setIsStartingChat(false);
    }
  };

  // Handle representation request actions
  const handleDismissRequest = async () => {
    setIsDismissingPlayer(true);
    try {
      const response = await playerService.respondToInvite(
        representationRequest.id,
        "decline"
      );
      if (response.success) {
        toast.success("Request declined successfully");
        setRepresentationRequest(null);
        // Clear the navigation state
        navigate(`/search/agent/${agentId}`, { replace: true });
      } else {
        toast.error(response.error || "Failed to decline request");
      }
    } catch (error) {
      console.error("Error declining request:", error);
      toast.error("Failed to decline request. Please try again.");
    } finally {
      setIsDismissingPlayer(false);
    }
  };

  const handleApproveRequest = async () => {
    setIsAcceptingPlayer(true);
    try {
      const response = await playerService.respondToInvite(
        representationRequest.id,
        "accept"
      );
      if (response.success) {
        toast.success("Request accepted successfully");
        setRepresentationRequest(null);
        // Clear the navigation state
        navigate(`/search/agent/${agentId}`, { replace: true });
      } else {
        toast.error(response.error || "Failed to accept request");
      }
    } catch (error) {
      console.error("Error accepting request:", error);
      toast.error("Failed to accept request. Please try again.");
    } finally {
      setIsAcceptingPlayer(false);
    }
  };

  // Handle agent join request actions with confirmation
  const handleAcceptAgentRequest = async () => {
    setIsAcceptingAgent(true);
    try {
      const response = await agentService.respondToAgentRequest(
        agentJoinRequest.id,
        "accept"
      );
      if (response.success) {
        toast.success("Agent request accepted successfully");
        setAgentJoinRequest(null);
        setShowAcceptConfirmModal(false);
        // Clear the navigation state
        navigate(`/search/agent/${agentId}`, { replace: true });
      } else {
        toast.error(response.error || "Failed to accept agent request");
      }
    } catch (error) {
      console.error("Error accepting agent request:", error);
      toast.error("Failed to accept agent request. Please try again.");
    } finally {
      setIsAcceptingAgent(false);
    }
  };

  const handleDeclineAgentRequest = async () => {
    setIsDecliningAgent(true);
    try {
      const response = await agentService.respondToAgentRequest(
        agentJoinRequest.id,
        "decline"
      );
      if (response.success) {
        toast.success("Agent request declined successfully");
        setAgentJoinRequest(null);
        setShowDeclineConfirmModal(false);
        // Clear the navigation state
        navigate(`/search/agent/${agentId}`, { replace: true });
      } else {
        toast.error(response.error || "Failed to decline agent request");
      }
    } catch (error) {
      console.error("Error declining agent request:", error);
      toast.error("Failed to decline agent request. Please try again.");
    } finally {
      setIsDecliningAgent(false);
    }
  };

  // Optimized filtering function
  const filterPlayersByPosition = useCallback((players, position) => {
    if (position === "all") return players;

    return players.filter((player) => {
      // Handle "unknown" position filter
      if (position === "unknown") {
        const hasPosition =
          player.position && player.position !== "Position not set";
        const hasDetailedPosition =
          player.tp_detailed_position &&
          Object.values(player.tp_detailed_position).some(
            (val) => val !== null && val !== ""
          );
        return !hasPosition && !hasDetailedPosition;
      }

      // First check tp_detailed_position
      if (player.tp_detailed_position) {
        const positionValues = Object.values(player.tp_detailed_position);
        const hasMatchingPosition = positionValues.some((val) => {
          if (!val) return false;
          const normalizedVal = val.toString().toLowerCase().trim();
          const normalizedPosition = position.toLowerCase().trim();
          return normalizedVal === normalizedPosition;
        });
        if (hasMatchingPosition) return true;
      }

      // Fallback to basic position field
      if (player.position) {
        const normalizedPlayerPos = player.position.toLowerCase().trim();
        const normalizedFilterPos = position.toLowerCase().trim();

        // Direct match
        if (normalizedPlayerPos === normalizedFilterPos) return true;

        // Mapping for common position names to detailed positions
        const positionMappings = {
          gk: ["gk"],
          goalkeeper: ["gk"],

          lb: ["lb", "lwb"],
          lcb: ["lcb", "cb"],
          cb: ["cb", "lcb", "rcb"],
          rcb: ["rcb", "cb"],
          rb: ["rb", "rwb"],
          defender: ["lb", "lcb", "cb", "rcb", "rb", "lwb", "rwb"],

          ldm: ["ldm", "dm"],
          dm: ["dm", "ldm", "rdm"],
          rdm: ["rdm", "dm"],
          lcm: ["lcm", "cm"],
          cm: ["cm", "lcm", "rcm"],
          rcm: ["rcm", "cm"],
          lam: ["lam", "am"],
          am: ["am", "lam", "ram"],
          ram: ["ram", "am"],
          midfielder: [
            "ldm",
            "dm",
            "rdm",
            "lcm",
            "cm",
            "rcm",
            "lam",
            "am",
            "ram",
          ],

          lw: ["lw"],
          rw: ["rw"],
          ls: ["ls", "st"],
          st: ["st", "ls", "rs"],
          rs: ["rs", "st"],
          forward: ["lw", "rw", "ls", "st", "rs"],
          attacker: ["lw", "rw", "ls", "st", "rs"],
          "center-forward": ["st", "ls", "rs"],
        };

        // Check if the player's position maps to the filter position
        const filterMappings = positionMappings[normalizedFilterPos] || [];
        if (filterMappings.includes(normalizedPlayerPos)) return true;

        // Check reverse mapping
        const playerMappings = positionMappings[normalizedPlayerPos] || [];
        if (playerMappings.includes(normalizedFilterPos)) return true;
      }

      return false;
    });
  }, []);

  // Filter players by position using the optimized function
  const filteredPlayers = useMemo(
    () => filterPlayersByPosition(agent?.players || [], selectedPosition),
    [agent?.players, selectedPosition, filterPlayersByPosition]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-red-500">
        Error: {error}
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">
        Agent not found.
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="w-full px-12 py-4 flex items-center justify-between border-b border-[#E5E6E8]">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8327 7.00033H1.16602M1.16602 7.00033L6.99935 12.8337M1.16602 7.00033L6.99935 1.16699"
                stroke="#1E1E1E"
                strokeWidth="1.875"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-xl font-bold text-[#222A35]">
              {agent.name}
            </span>
          </div>
          {(() => {
            // Show reject/accept buttons if representation request exists (not for agents)
            if (representationRequest && currentRole !== ROLES.Agent) {
              return (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDismissRequest}
                    disabled={isDismissingPlayer}
                    className={`px-4 py-2 border border-[#E6E6E6] bg-white flex items-center gap-2 text-[#1E1E1E] rounded-lg ${
                      isDismissingPlayer
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {isDismissingPlayer ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-600"></div>
                        <span>Rejecting...</span>
                      </>
                    ) : (
                      <>
                        <span>X</span>
                        <span>Reject</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleApproveRequest}
                    disabled={isAcceptingPlayer}
                    className={`px-4 py-2 rounded-lg bg-[#38AB75] text-white flex items-center gap-2 ${
                      isAcceptingPlayer
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-[#2d8a5f]"
                    }`}
                  >
                    {isAcceptingPlayer ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Accepting...</span>
                      </>
                    ) : (
                      <>
                        <span>✓</span>
                        <span>Accept Agent & Proceed</span>
                      </>
                    )}
                  </button>
                </div>
              );
            }

            // Show accept/decline buttons if agent join request exists (for agency admins)
            if (agentJoinRequest && currentRole === ROLES.AgencyAdmin) {
              return (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeclineConfirmModal(true)}
                    disabled={isDecliningAgent}
                    className={`px-4 py-2 border border-[#E6E6E6] bg-white flex items-center gap-2 text-[#1E1E1E] rounded-lg ${
                      isDecliningAgent
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <span>X</span>
                    <span>Decline</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAcceptConfirmModal(true)}
                    disabled={isAcceptingAgent}
                    className={`px-4 py-2 rounded-lg bg-[#38AB75] text-white flex items-center gap-2 ${
                      isAcceptingAgent
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-[#2d8a5f]"
                    }`}
                  >
                    <span>✓</span>
                    <span>Accept Agent</span>
                  </button>
                </div>
              );
            }

            // Show Message button only for ClubAdmin and Agent roles
            // For Agent role, hide if viewing own profile
            if (currentRole === ROLES.ClubAdmin) {
              return (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-lg px-4 py-2 flex items-center gap-2 bg-[#2F59E8] text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#2649bc]"
                    onClick={handleStartChat}
                    disabled={isStartingChat}
                    aria-label="Message agent"
                  >
                    {isStartingChat ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                        <span>Starting...</span>
                      </>
                    ) : (
                      <span>Message</span>
                    )}
                  </button>
                </div>
              );
            }

            // For Agent role, only show if not viewing own profile.
            // Agent->agent is a COLD CONTACT: it opens the Message Request
            // compose dialog (design 1i) instead of an instant chat, and flips
            // to "Request pending" (1l) once sent.
            if (currentRole === ROLES.Agent && user?.id !== agent.id) {
              return (
                <div className="flex items-center gap-2">
                  {requestSent ? (
                    <RequestPendingBadge />
                  ) : (
                    <button
                      type="button"
                      className="rounded-lg px-4 py-2 flex items-center gap-2 bg-[#2F59E8] text-white transition-colors hover:bg-[#2649bc]"
                      onClick={() => setShowComposeModal(true)}
                      aria-label="Message agent"
                    >
                      <span>Message</span>
                    </button>
                  )}
                </div>
              );
            }

            // Show share button for all other roles
            return (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="border border-[#E6E6E6] rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                  onClick={() => setShowShareModal(true)}
                  aria-label="Share agent profile"
                >
                  <svg
                    width="14"
                    height="16"
                    viewBox="0 0 14 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.72667 9.00634L9.28 11.6597M9.27333 4.33967L4.72667 6.99301M13 3.33301C13 4.43758 12.1046 5.33301 11 5.33301C9.89543 5.33301 9 4.43758 9 3.33301C9 2.22844 9.89543 1.33301 11 1.33301C12.1046 1.33301 13 2.22844 13 3.33301ZM5 7.99967C5 9.10424 4.10457 9.99967 3 9.99967C1.89543 9.99967 1 9.10424 1 7.99967C1 6.8951 1.89543 5.99967 3 5.99967C4.10457 5.99967 5 6.8951 5 7.99967ZM13 12.6663C13 13.7709 12.1046 14.6663 11 14.6663C9.89543 14.6663 9 13.7709 9 12.6663C9 11.5618 9.89543 10.6663 11 10.6663C12.1046 10.6663 13 11.5618 13 12.6663Z"
                      stroke="#2C2C2C"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-[#1E1E1E]">Share</span>
                </button>
              </div>
            );
          })()}
        </div>

        {/* Hero Section Banner */}
        <div className="w-full px-10 pt-6">
          <AgentHeroBanner agent={agent} />
        </div>

        {/* Managing Players Section */}
        <div className="w-full mx-auto px-12 py-8 bg-white">
          <h2 className="text-[16px] font-medium text-[#1E1E1E] mb-6">
            Managing Players
          </h2>

          {/* Positions Dropdown */}
          <div className="mb-6">
            <PositionFilterDropdown
              positions={positions}
              selectedId={selectedPosition}
              onSelect={setSelectedPosition}
            />
          </div>

          {/* Players Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPlayers.map((player) => (
              <AgentPlayerCard
                key={player.account_id}
                player={player}
                showStatus={false}
                showQuickActions={false}
                showTransferFee={false}
              />
            ))}
          </div>

          {filteredPlayers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No players found for the selected position.
              </p>
            </div>
          )}
        </div>

        <ShareActionSheet
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onShare={handleShare}
          title={`Share ${agent?.name || "Agent"} Profile`}
          shareUrl={shareUrl}
          shareType="agent_public_profile"
          shareData={{
            agent: {
              id: agent?.id,
              name: agent?.name || "Agent Name",
            },
          }}
        />

        {showComposeModal && (
          <MessageRequestComposeModal
            recipient={{
              agent_id: agent.agentPk,
              name: agent.name,
              agency: agent.agency?.name,
              subtitle: [agent.agency?.name, agent.country]
                .filter(Boolean)
                .join(" · "),
              profile_picture_url: agent.profile_picture_url,
            }}
            onClose={() => setShowComposeModal(false)}
            onSent={() => setRequestSent(true)}
            onSameOrg={({ channelId, channelType }) =>
              navigate("/inbox", {
                state: { directChat: { channelId, channelType } },
              })
            }
            /* Frame 1r's escape hatch. `requests=sent` opens the gate's Sent
               view (the sender's open outbox); `tab=dm` picks a lane that hosts
               the gate — the Sent list itself is account-wide, so its count is
               the same N the link promises. The old `?tab=requests` pointed at a
               lane key that does not exist, so the link went nowhere. */
            onViewPending={() => navigate("/inbox?tab=dm&requests=sent")}
          />
        )}
      </div>

      {/* Accept Agent Confirmation Modal */}
      {showAcceptConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Accept Agent Request
              </h3>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-6">
                <div className="w-16 h-16 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0">
                  {agent?.profile_picture_url && (
                    <img
                      src={agent.profile_picture_url}
                      alt={agent.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {agent?.name || "Agent"}
                  </h4>
                  <p className="text-sm text-gray-600">Agent Join Request</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to accept this agent's request to join
                your agency? This will add them to your agency roster.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAcceptConfirmModal(false)}
                  disabled={isAcceptingAgent}
                  className={`flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 ${
                    isAcceptingAgent
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAcceptAgentRequest}
                  disabled={isAcceptingAgent}
                  className={`flex-1 py-2 px-4 bg-[#38AB75] text-white rounded-lg flex items-center justify-center gap-2 ${
                    isAcceptingAgent
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-[#2d8a5f]"
                  }`}
                >
                  {isAcceptingAgent ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      <span>Accepting...</span>
                    </>
                  ) : (
                    "Accept Agent"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Agent Confirmation Modal */}
      {showDeclineConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Decline Agent Request
              </h3>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-6">
                <div className="w-16 h-16 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0">
                  {agent?.profile_picture_url && (
                    <img
                      src={agent.profile_picture_url}
                      alt={agent.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {agent?.name || "Agent"}
                  </h4>
                  <p className="text-sm text-gray-600">Agent Join Request</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to decline this agent's request to join
                your agency? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeclineConfirmModal(false)}
                  disabled={isDecliningAgent}
                  className={`flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 ${
                    isDecliningAgent
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeclineAgentRequest}
                  disabled={isDecliningAgent}
                  className={`flex-1 py-2 px-4 bg-red-600 text-white rounded-lg flex items-center justify-center gap-2 ${
                    isDecliningAgent
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-red-700"
                  }`}
                >
                  {isDecliningAgent ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      <span>Declining...</span>
                    </>
                  ) : (
                    "Decline Request"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentPublicProfile;
